// frontend/src/components/wizard/StepReview/SyncModePanel.jsx
import React, { useEffect } from 'react';
import { CheckCircle2, AlertTriangle, ArrowDownUp } from 'lucide-react';
import { useWizardStore } from '../../../store/useWizardStore';
import { useLanguageStore } from '../../../store/useLanguageStore';

const FLAT_TYPES = ['csv', 'excel', 'googlesheets', 'dbf'];
const APPEND_ONLY_TYPES = ['rest_api', 'defontana'];

const SYNC_MODES = [
  { value: 'append', colorRing: 'ring-emerald-600', colorBorder: 'border-emerald-600', colorBg: 'bg-emerald-50', colorIcon: 'text-emerald-600' },
  { value: 'overwrite', colorRing: 'ring-red-600', colorBorder: 'border-red-600', colorBg: 'bg-red-50', colorIcon: 'text-red-600' },
  { value: 'upsert', colorRing: 'ring-blue-600', colorBorder: 'border-blue-600', colorBg: 'bg-blue-50', colorIcon: 'text-blue-600' },
];

export const SyncModePanel = () => {
  const { syncMode, setSyncMode, target, targetSelectedTables } = useWizardStore();
  // FK relationships en destino → tablas hijas que se verán afectadas por OVERWRITE
  const targetFkRelationships = (target?.relationships || []).filter(r => r.joinType === 'FK');
  const hasFkRelationships = targetFkRelationships.length > 0;
  const fkChildTables = [...new Set(targetFkRelationships.map(r => r.tableTo))];

  const { t } = useLanguageStore();

  const isTargetFlat = FLAT_TYPES.includes(target?.type?.toLowerCase());
  const isAppendOnly = APPEND_ONLY_TYPES.includes(target?.type?.toLowerCase());

  // Reset defensivo: si llega con upsert + destino plano, corregir a append
  useEffect(() => {
    if (isTargetFlat && syncMode === 'upsert') setSyncMode('append');
    if (isAppendOnly && syncMode !== 'append') setSyncMode('append');
  }, []);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
      <h3 className="font-bold text-slate-800 flex items-center mb-4">
        <ArrowDownUp className="w-5 h-5 mr-2 text-indigo-600" />
        {t('wizard.step_target.sync_strategy') || 'Modalidad de Escritura'}
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {SYNC_MODES
          .filter(({ value }) => !(isTargetFlat && value === 'upsert')) // ← mismo filtro que StepTarget
          .filter(({ value }) => !(isAppendOnly && value !== 'append'))
          .map(({ value, colorRing, colorBorder, colorBg, colorIcon }) => {
            const isSelected = syncMode === value;
            return (
              <label
                key={value}
                className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all ${isSelected
                  ? `${colorBorder} ${colorBg} ring-1 ${colorRing} shadow-sm`
                  : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
              >
                <input
                  type="radio"
                  value={value}
                  checked={isSelected}
                  onChange={() => setSyncMode(value)}
                  className="sr-only"
                />
                <span className="font-bold text-sm text-slate-800 block mb-1">
                  {t(`wizard.sync_modes.${value}`)}
                </span>
                <p className="text-xs text-slate-500 leading-relaxed">
                  {t(`wizard.sync_modes.${value}_desc`)}
                </p>
                {isSelected && (
                  <CheckCircle2 className={`absolute top-3 right-3 w-4 h-4 ${colorIcon}`} />
                )}
              </label>
            );
          })}
      </div>

      {/* Advertencias contextuales */}
      {/* {syncMode === 'overwrite' && (
        <div className="mt-3 p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-start animate-in fade-in">
          <AlertTriangle className="w-4 h-4 text-red-500 mr-2 shrink-0 mt-0.5" />
          <p className="text-xs text-red-700 font-medium leading-relaxed">
            {isTargetFlat
              ? t('wizard.alerts.overwrite_desc_flat') || 'El archivo o hoja será borrado completamente y recreado desde cero con los datos del origen.'
              : t('wizard.alerts.overwrite_desc')
            }
          </p>
        </div>
      )} */}

      {syncMode === 'overwrite' && (
        <div className="mt-3 p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-start animate-in fade-in">
          <AlertTriangle className="w-4 h-4 text-red-500 mr-2 shrink-0 mt-0.5" />
          <div className="text-xs text-red-700 font-medium leading-relaxed">
            <p>
              {isTargetFlat
                ? t('wizard.alerts.overwrite_desc_flat') || 'El archivo o hoja será borrado completamente y recreado desde cero con los datos del origen.'
                : t('wizard.alerts.overwrite_desc')
              }
            </p>
            {/* Advertencia adicional si hay tablas hijas con FK */}
            {!isTargetFlat && hasFkRelationships && (
              <p className="mt-2 pt-2 border-t border-red-200">
                {t('wizard.alerts.overwrite_fk_warning') ||
                  'Además, los registros existentes en las tablas dependientes'}{' '}
                <strong>{fkChildTables.join(', ')}</strong>{' '}
                {t('wizard.alerts.overwrite_fk_warning_suffix') ||
                  'también serán eliminados antes de recargar, ya que tienen relaciones FK con la tabla destino.'}
              </p>
            )}
          </div>
        </div>
      )}


      {syncMode === 'upsert' && !isTargetFlat && ( // ← nunca se muestra para flat
        <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg flex items-start animate-in fade-in">
          <AlertTriangle className="w-4 h-4 text-blue-500 mr-2 shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700 font-medium leading-relaxed">
            {t('wizard.alerts.upsert_desc')}
          </p>
        </div>
      )}
    </div>
  );
};